export class CodeInspector {
  constructor(devtools) {
    this.devtools = devtools;
    this.patterns = this.initializePatterns();
  }

  initializePatterns() {
    return {
      // Common JavaScript issues
      undefinedVariable: /ReferenceError.*is not defined/,
      typeError: /TypeError.*is not a function|Cannot read propert/,
      nullReference: /Cannot read property.*of null|Cannot read property.*of undefined/,
      asyncAwaitIssue: /SyntaxError.*await.*async/,
      promiseRejection: /UnhandledPromiseRejectionWarning/,
      
      // Performance issues
      infiniteLoop: /Maximum call stack size exceeded/,
      memoryLeak: /out of memory/i,
      
      // Security issues
      xssVulnerability: /script.*onerror|javascript:|eval\(/i,
      
      // Common mistakes
      equalityOperator: /==.*null|==.*undefined/,
      missingReturn: /function.*{[^}]*}(?!.*return)/,
      unusedVariable: /var\s+\w+.*=.*;\s*(?!.*\w+)/
    };
  }

  async analyzeError(errorData) {
    const analysis = {
      severity: this.calculateSeverity(errorData),
      category: this.categorizeError(errorData),
      potentialCauses: this.identifyPotentialCauses(errorData),
      suggestions: this.generateSuggestions(errorData),
      relatedErrors: this.findRelatedErrors(errorData),
      codeQuality: await this.analyzeCodeQuality(errorData),
      performance: this.analyzePerformanceImpact(errorData)
    };

    return analysis;
  }

  calculateSeverity(errorData) {
    const criticalPatterns = [
      /Maximum call stack size exceeded/,
      /out of memory/i,
      /Script error/,
      /Network Error/
    ];

    const highPatterns = [
      /TypeError/,
      /ReferenceError/,
      /SyntaxError/
    ];

    const message = errorData.message || '';
    
    if (criticalPatterns.some(pattern => pattern.test(message))) {
      return { level: 'critical', score: 10, color: '#ff0000' };
    }
    
    if (highPatterns.some(pattern => pattern.test(message))) {
      return { level: 'high', score: 7, color: '#ff6600' };
    }
    
    if (errorData.type === 'console' && errorData.level === 'error') {
      return { level: 'medium', score: 5, color: '#ffaa00' };
    }
    
    return { level: 'low', score: 3, color: '#ffff00' };
  }

  categorizeError(errorData) {
    const message = errorData.message || '';
    const stack = errorData.stackTrace?.join(' ') || '';
    
    if (this.patterns.undefinedVariable.test(message)) {
      return { type: 'Variable', subtype: 'Undefined Reference' };
    }
    
    if (this.patterns.typeError.test(message)) {
      return { type: 'Type', subtype: 'Invalid Operation' };
    }
    
    if (this.patterns.nullReference.test(message)) {
      return { type: 'Null/Undefined', subtype: 'Property Access' };
    }
    
    if (this.patterns.infiniteLoop.test(message)) {
      return { type: 'Performance', subtype: 'Stack Overflow' };
    }
    
    if (this.patterns.promiseRejection.test(message)) {
      return { type: 'Async', subtype: 'Unhandled Promise' };
    }
    
    return { type: 'General', subtype: 'Unknown' };
  }

  identifyPotentialCauses(errorData) {
    const causes = [];
    const message = errorData.message || '';
    const source = errorData.source || '';
    
    // Analyze common patterns
    if (message.includes('is not defined')) {
      const variable = message.match(/'([^']+)' is not defined/)?.[1];
      causes.push({
        type: 'Missing Declaration',
        description: `Variable '${variable}' is used but not declared`,
        likelihood: 0.9,
        fixes: [
          `Declare the variable: let ${variable} = ...`,
          `Check for typos in variable name`,
          `Ensure the script containing '${variable}' is loaded`
        ]
      });
    }
    
    if (message.includes('is not a function')) {
      causes.push({
        type: 'Type Mismatch',
        description: 'Attempting to call a non-function value',
        likelihood: 0.8,
        fixes: [
          'Check if the variable is actually a function',
          'Verify the object has the expected method',
          'Add type checking before function calls'
        ]
      });
    }
    
    if (message.includes('Cannot read property')) {
      causes.push({
        type: 'Null/Undefined Access',
        description: 'Trying to access property of null or undefined',
        likelihood: 0.85,
        fixes: [
          'Add null/undefined checks before property access',
          'Use optional chaining: obj?.property',
          'Initialize the object before use'
        ]
      });
    }
    
    return causes;
  }

  generateSuggestions(errorData) {
    const suggestions = [];
    const category = this.categorizeError(errorData);
    
    switch (category.type) {
      case 'Variable':
        suggestions.push({
          title: 'Use Strict Mode',
          description: 'Add "use strict"; to catch undeclared variables',
          code: '"use strict";\n// Your code here',
          priority: 'high'
        });
        break;
        
      case 'Type':
        suggestions.push({
          title: 'Add Type Checking',
          description: 'Validate types before operations',
          code: 'if (typeof myVar === "function") {\n  myVar();\n}',
          priority: 'medium'
        });
        break;
        
      case 'Null/Undefined':
        suggestions.push({
          title: 'Safe Property Access',
          description: 'Use optional chaining or null checks',
          code: 'const value = obj?.property || defaultValue;',
          priority: 'high'
        });
        break;
        
      case 'Performance':
        suggestions.push({
          title: 'Add Loop Guards',
          description: 'Prevent infinite loops with counters',
          code: 'let counter = 0;\nwhile (condition && counter++ < 1000) {\n  // loop body\n}',
          priority: 'critical'
        });
        break;
    }
    
    return suggestions;
  }

  findRelatedErrors(errorData) {
    const allErrors = this.devtools.runtimeManager.errors || [];
    const related = [];
    
    allErrors.forEach(error => {
      if (error.id === errorData.id) return;
      
      // Same error type
      if (error.message === errorData.message) {
        related.push({
          error,
          relationship: 'duplicate',
          confidence: 1.0
        });
      }
      
      // Same source file
      if (error.source && errorData.source && error.source === errorData.source) {
        related.push({
          error,
          relationship: 'same-file',
          confidence: 0.7
        });
      }
      
      // Similar stack trace
      if (this.compareStackTraces(error.stackTrace, errorData.stackTrace) > 0.6) {
        related.push({
          error,
          relationship: 'similar-stack',
          confidence: 0.8
        });
      }
    });
    
    return related.slice(0, 5); // Limit to 5 most relevant
  }

  compareStackTraces(stack1, stack2) {
    if (!stack1 || !stack2) return 0;
    
    const lines1 = stack1.slice(0, 3); // Compare first 3 lines
    const lines2 = stack2.slice(0, 3);
    
    let matches = 0;
    const maxLength = Math.max(lines1.length, lines2.length);
    
    for (let i = 0; i < Math.min(lines1.length, lines2.length); i++) {
      if (lines1[i] === lines2[i]) matches++;
    }
    
    return matches / maxLength;
  }

  async analyzeCodeQuality(errorData) {
    // Simulate code quality analysis
    const issues = [];
    const source = errorData.source || '';
    
    // Check for common anti-patterns in error context
    if (errorData.message?.includes('eval')) {
      issues.push({
        type: 'security',
        message: 'Use of eval() detected - security risk',
        severity: 'high'
      });
    }
    
    if (errorData.stackTrace?.some(line => line.includes('setTimeout'))) {
      issues.push({
        type: 'performance',
        message: 'Error in setTimeout - consider using requestAnimationFrame',
        severity: 'medium'
      });
    }
    
    return {
      score: Math.max(0, 100 - (issues.length * 20)),
      issues,
      recommendations: [
        'Add error boundaries for better error handling',
        'Implement proper logging and monitoring',
        'Use TypeScript for better type safety'
      ]
    };
  }

  analyzePerformanceImpact(errorData) {
    const impact = {
      level: 'low',
      metrics: {},
      recommendations: []
    };
    
    if (errorData.message?.includes('Maximum call stack')) {
      impact.level = 'critical';
      impact.metrics.stackOverflow = true;
      impact.recommendations.push('Fix infinite recursion immediately');
    }
    
    if (errorData.type === 'promise' && errorData.message?.includes('timeout')) {
      impact.level = 'medium';
      impact.metrics.networkDelay = true;
      impact.recommendations.push('Implement proper timeout handling');
    }
    
    return impact;
  }
}